





















































import {useStore} from "@/utils/use-store";
import {computed, defineComponent, onBeforeMount, reactive, toRefs, ref, watch} from "@vue/composition-api";
import {ServiceFactory} from '@/service/ServiceFactory';
import {Exercise, Homework} from '@/helpers/types'

const ExerciseService = ServiceFactory.get('exercise');
const HomeworkService = ServiceFactory.get('homework');

export default defineComponent({
  name: "HomeworkForm",

  props: {
    mode: {
      type: String,
      default: 'create'
    },
    homeworkToEdit: {
      type: Object,
      default: () => ({})
    },
    isDrawerOpened: {
      type: Boolean,
      default: false
    }
  },

  setup(props, context) {
    const store = useStore();
    const {accessToken: token} = store.state.user;
    const {
      activeExercises,
      allExercises,
      fetchAllExercises,
      exercisesList,
      removeExercise,
      submitHomework,
      closeDrawer,
      newExercises,
      search,
      loading,
    } = useHomeworkCRUD(props, context, token);

    async function beforeMountEventHandler() {
      try {
        await fetchAllExercises(token);
      } catch (e) {
        console.error(e);
      }
    }

    onBeforeMount(beforeMountEventHandler);

    return {
      allExercises,
      activeExercises,
      exercisesList,
      removeExercise,
      submitHomework,
      closeDrawer,
      newExercises,
      search,
      loading
    };
  }
});

function useHomeworkCRUD(props:any, context:any, token: string) {
  const newExercises = ref([]);
  const allExercisesSource = ref([]);
  const search = ref('');
  const loading = ref(false);

  const homework = reactive({
    activeExercises: null,
  });

  let searchTimeout: number;
  watch(search, (value) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      if (value) fetchAllExercises(token, value);
    }, 300) as unknown as number;
  });

  const exercisesList = computed({
    get: () => {
      return props && props.homeworkToEdit && props.homeworkToEdit.exercise ? [ ...props.homeworkToEdit.exercise, ...newExercises.value ] : newExercises.value
    },
    set: (val:any) => {
      newExercises.value = [...newExercises.value, val as never];
    }
  });

  const allExercises = computed(() => {
    let checkedExercisesIds = exercisesList.value.map((item:Homework) => item.id);
    return allExercisesSource.value.filter((item:Homework) => !checkedExercisesIds.includes(item.id));
  });

  watch(() => props.isDrawerOpened, (drawerState) => {
    if(!drawerState) {
      newExercises.value = [];
    }
  });

  function removeExercise(id:string|number) {
    if (props.homeworkToEdit.exercise.some((item:Exercise) => item.id === id)) {
      context.emit('removeExercise', id);
    } else {
      newExercises.value = newExercises.value.filter((item: any) => item.id !== id);
    }
  }

  async function fetchAllExercises(token: string, search?: string) {
    loading.value = true;
    let params: any = {};
    if (search) {
      params = {
        keyword: search,
      };
    }
    try {
      const {data} = await ExerciseService.paginatedList(token, params);
      allExercisesSource.value = data;
    } catch (e) {
      const message: string = e.response ? e.response.data.message || e.response.data.error : e.message;
      throw Error(message);
    } finally {
      loading.value = false;
    }
  }

  async function submitHomework() {
    const exercisesIds = exercisesList.value.map((item:Homework) => item.id);
    try {
      if(props.mode === 'create') {
        let payload = props.homeworkToEdit;
        payload.exercise = exercisesIds;
        await HomeworkService.create({ token: token, payload: payload });
        context.emit('updateTable');
      } else if(props.mode === 'edit') {
        await HomeworkService.edit({ id: props.homeworkToEdit.id, token: token, payload: { exercise: exercisesIds } });
        context.emit('updateTable');
      }
      closeDrawer();
    } catch (e) {
      const message: string = e.response ? e.response.data.message || e.response.data.error : e.message;
      throw Error(message);
    }
  }

  function closeDrawer() {
    context.emit('close');
  }

  return {
    ...toRefs(homework),
    allExercises,
    fetchAllExercises,
    exercisesList,
    removeExercise,
    submitHomework,
    closeDrawer,
    newExercises,
    search,
    loading,
  };
}
